import { FormHelperText } from '@mui/material';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import axios from 'axios';
import * as React from 'react';

type Props = Omit<
    TextFieldProps,
    'name' | 'value' | 'select' | 'SelectProps' | 'onChange' | 'onBlur'
> & {
    /* TIP: fieldName property will be used as the object key in the onChangeHandler reducer */
    fieldName: string;
    fieldValue: any;
    onChangeHandler: React.Dispatch<{ [key in string]: string }>;
    formErrors: unknown;
};

/**
 * TextFieldFast
 *
 * @description
 * A wrapper for Mui-TextField component which prevents updates to the parent component's state
 * until the user has focused away from the input.
 *
 * @hidden
 * Useful in large forms where updates to a parent component on each keypress could cause poor
 * user experience due to slow feedback
 */
export default function ContactFormNamesField({
    fieldName,
    fieldValue,
    onChangeHandler,
    formErrors,
    ...props
}: Props) {
    const [text, setText] = React.useState<string>(fieldValue);

    const errorText = React.useMemo(() => {
        if (!formErrors) return undefined;

        if (axios.isAxiosError(formErrors)) {
            return formErrors.response?.data[fieldName];
        }
    }, [fieldName, formErrors]);

    React.useEffect(() => {
        if (fieldValue !== text) {
            setText(fieldValue);
        }
    }, [fieldValue]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setText(event.target.value);
    };

    const handleBlur = () => {
        onChangeHandler({ [fieldName]: text });
    };

    return (
        <>
            <TextField
                {...(props as TextFieldProps)}
                error={!!errorText}
                name={fieldName}
                value={text}
                onChange={handleChange}
                onBlur={handleBlur}
            />
            {errorText && <FormHelperText error>{errorText}</FormHelperText>}
        </>
    );
}
